import { Component, OnInit, OnDestroy } from '@angular/core';
import * as Highcharts from 'highcharts';
import { Chart } from 'angular-highcharts';
import { HomeService } from 'src/app/services/services/home.service';
import { homeDetails, listQueues, productInformations, queueMonitorData, visualQueueTimeline } from '../models/home-models';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { interval, Subject, takeUntil, forkJoin, tap, switchMap } from 'rxjs';
import { I18NextPipe } from 'angular-i18next';
import { EnvironmentService } from 'src/app/services/services/environmet.service';


@Component({
  selector: 'app-home',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  standalone: false
})
export class HomePageComponent implements OnInit, OnDestroy {
  ajaxRefreshInterval: number;
  errorMessages: string[] = [];
  tooltipMessages: any = {};
  lang: any = {};
  listQueues: listQueues[] = [];
  selectedQueueIds: number[] = [];
  supportInformation: any;
  productInformations: productInformations[];
  passwordExpired: boolean = false;
  statisticCountryCode: string = '';
  session: any;
  userGroup: string = '';
  editInformation: boolean = false;
  editInformationUrl = '/Home/EditInformation';
  information: any[] = [];
  defTimeInterval: number = 8;
  baseUrl: string;
  customerKey: string = '';

  chartData: any[] = [];
  chartLabels: string[] = [];
  chartOptions = { responsive: true };
  queueMonitorData: queueMonitorData[] = [];
  timelineData: any[] = [];

  chart: Chart;

  categories = Array.from({ length: 96 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  });

  private destroy$ = new Subject<void>();

  constructor(
    private homeService: HomeService,
    private sanitizer: DomSanitizer,
    private i18n: I18NextPipe,) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.editInformationUrl = EnvironmentService.environment.baseUrl + this.editInformationUrl;
    this.homeService.getHomePage()
      .pipe(
        tap(data => {
          this.customerKey = data.customerKey;
          this.ajaxRefreshInterval = data.ajaxRefreshInterval;
          this.editInformation = data.editInformation;
          this.listQueues = data.listQueues;
          this.passwordExpired = data.passwordExpired !== 0;
          this.productInformations = data.productInfomations;
          this.statisticCountryCode = data.statisticCountryCode;
          this.userGroup = data.userGroupName;

          this.restoreQueueSelection(data.selectListQueueIds);
        })
      )
      .subscribe(() => {
        this.loadDashboardData();
        this.startMonitorAutoRefresh(); // Monitor data interval update
        this.startChartAutoRefresh();  // Chart data interval update
      });
  }

  buildChart() {
    const series = Array.from({ length: 96 }, () => ({
      offered: 0,
      answered: 0
    }));

    this.timelineData.forEach(entry => {
      const d = new Date(entry.timestamp);
      const bucket = this.get15MinBucketIndex(d);

      series[bucket].offered += entry.callsOffered;
      series[bucket].answered += entry.callsAnswered;
    });

    const now = new Date();
    let currentIndex = now.getHours() * 4 + Math.floor(now.getMinutes() / 15);
    currentIndex = currentIndex - 1;

    // Handle midnight edge case
    if (currentIndex < 0) {
      currentIndex = 95;
    }

    const WINDOW_15 = 32; // 8 hours = 32×15 mins
    let startIndex = Math.max(0, currentIndex - (WINDOW_15 - 1));

    const slicedCategories = this.categories.slice(startIndex, currentIndex + 1);
    const slicedSeriesData = series.slice(startIndex, currentIndex + 1);

    const offeredMarkers = slicedCategories.map(label => ({
      radius: label.endsWith(':00') ? 6 : 3,
      symbol: 'circle'
    }));

    const answeredMarkers = slicedCategories.map(label => ({
      radius: label.endsWith(':00') ? 6 : 3,
      symbol: 'circle'
    }));

    var that = this;
    this.chart = new Chart({
      chart: {
        type: 'line',
        backgroundColor: '#ffffff'
      },

      title: {
        text: '',
        align: 'left'
      },

      xAxis: {
        categories: slicedCategories,
        tickInterval: 1,
        labels: {
          formatter: function () {
            const cat = this.axis.categories[this.pos] as any;
            const label = cat?.name;

            if (typeof label === 'string' && label.endsWith(':00')) {
              return label;
            }

            return '';
          }
        }
      },
      yAxis: {
        title: {
          text: ""
        }
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: '#ffffff',
        borderColor: '#EED9FA',
        borderRadius: 6,
        borderWidth: 1,
        shadow: {
          color: 'rgba(0, 0, 0, 0.1)',
          offsetX: 0,
          offsetY: 1,
          opacity: 1,
          width: 4
        },
        padding: 12,
        style: {
          fontSize: '13px',
          fontWeight: '400'
        },
        formatter: function () {
          const idx = this.points[0].point.index;
          const interval = that.getQuarterlyIntervals(startIndex + idx);

          return `<b>${that.i18n.transform(`home:TotalAnswered`)}: ${this.points[1]?.y ?? 0}</b><br><b>${that.i18n.transform(`home:TotalOffered`)}: ${this.points[0].y}</b><br>${interval}`;
        }
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'top',
        itemStyle: {
          fontFamily: 'Poppins',
          fontSize: '12px',
          fontWeight: '400'
        }
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      series: [
        {
          name: this.i18n.transform(`home:TotalOffered`),
          type: 'areaspline',
          color: '#7E4797',
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(126, 71, 151, 0.35)'],
              [1, 'rgba(126, 71, 151, 0.00)']
            ]
          },
          data: slicedSeriesData.map((b, i) => ({
            y: b.offered,
            marker: offeredMarkers[i]
          }))
        },
        {
          name: this.i18n.transform(`home:TotalAnswered`),
          type: 'areaspline',
          color: '#FF375B',
          fillColor: {
            linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, 'rgba(255, 55, 91, 0.35)'],
              [1, 'rgba(255, 55, 91, 0.00)']
            ]
          },
          data: slicedSeriesData.map((b, i) => ({
            y: b.answered,
            marker: answeredMarkers[i]
          }))
        }
      ],
      plotOptions: {
        areaspline: {
          fillOpacity: 1,
          lineWidth: 3,
          stacking: undefined
        },
        series: {
          states: {
            hover: {
              lineWidthPlus: 0
            }
          }
        }
      }
    });
  }

  closeDriftInfo(): void {
    this.supportInformation = null;
  }

  onQueueSelectionChange(): void {
    localStorage.setItem(
      this.getStorageKey(this.customerKey),
      JSON.stringify(this.selectedQueueIds)
    );

    this.loadDashboardData();
  }

  cleanHtml(html: string): string {
    if (!html) return '';

    return html.replace(/href="\s*([^"]+)\s*"/g, (_, url) => {
      return `href="${url.trim()}"`;
    });
  }

  formatDateTime(d: Date): string {
    const pad = (n: number) => (n < 10 ? '0' + n : n);

    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();

    const hour = pad(d.getHours());
    const minute = pad(d.getMinutes());

    return `${day}-${month}-${year} ${hour}:${minute}`;
  }

  decodeHtml(s: string): string {
    const t = document.createElement('textarea');
    t.innerHTML = s;
    return t.value;
  }

  getDisplayValue(value: any): string {
    const decoded = this.decodeHtml(value);
    const normalized = this.normalizeDashes(decoded);

    return normalized === '' || normalized == null ? '-' : normalized;
  }

  private normalizeDashes(text: string): string {
    return text?.replace(/[\u2010-\u2015]/g, '-');
  }

  get15MinBucketIndex(date: Date) {
    const hour = date.getHours();
    const minute = date.getMinutes();
    return hour * 4 + Math.floor(minute / 15);
  }

  getQuarterlyIntervals(index: number) {
    const start = this.categories[index];
    const end = this.categories[(index + 1) % 96];
    return `${start} - ${end}`; // →
  }

  translateLabel(label) {
    return this.i18n.transform(`home:${label}`);
  }

  editInfoRedirect() {
    window.location.href = this.editInformationUrl;
  }

  private buildTimelineModel(queueIds: number[]): visualQueueTimeline {
    const dteTo = new Date();
    let dteFrom = new Date();

    dteFrom.setHours(dteFrom.getHours() - this.defTimeInterval);

    const zeroDate = new Date(
      dteTo.getFullYear(),
      dteTo.getMonth(),
      dteTo.getDate()
    );

    if (dteFrom.getTime() < zeroDate.getTime()) {
      dteFrom = zeroDate;
    }

    return {
      qIds: queueIds.join(","),
      dteFrom: this.formatDateTime(dteFrom),
      dteTo: this.formatDateTime(dteTo)
    };
  }

  private loadDashboardData(): void {

    if (!this.selectedQueueIds?.length) {
      this.timelineData = [];
      this.queueMonitorData = [];
      this.buildChart();
      return;
    }

    const model = this.buildTimelineModel(this.selectedQueueIds);

    forkJoin({
      monitorData: this.homeService.getAvailableQueueMonitorData(
        this.selectedQueueIds.join(",")
      ),
      timelineData: this.homeService.getVisualQueueTimeline(model)
    }).subscribe(result => {
      this.queueMonitorData = result.monitorData.returnObject;
      this.timelineData = result.timelineData.returnObject;
      this.buildChart();
    });
  }

  private restoreQueueSelection(defaultIds: number[]): void {
    const saved = localStorage.getItem(this.getStorageKey(this.customerKey));

    if (saved) {
      try {
        this.selectedQueueIds = JSON.parse(saved);
      } catch {
        this.selectedQueueIds = defaultIds;
      }
    } else {
      this.selectedQueueIds = defaultIds;
    }
  }

  private getStorageKey(custKey): string {
    return `home.selectedQueueIds.${custKey}`;
  }

  private startMonitorAutoRefresh(): void {
    if (!this.ajaxRefreshInterval || this.ajaxRefreshInterval <= 0) {
      return;
    }

    interval(this.ajaxRefreshInterval)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {

          if (!this.selectedQueueIds?.length) {
            return [];
          }

          return this.homeService.getAvailableQueueMonitorData(
            this.selectedQueueIds.join(",")
          );
        })
      )
      .subscribe((result: any) => {
        if (result?.returnObject) {
          this.queueMonitorData = result.returnObject;
        }
      });
  }

  private startChartAutoRefresh(): void {
    const CHART_REFRESH_MS = 300000; // 5 minutes

    interval(CHART_REFRESH_MS)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {

          if (!this.selectedQueueIds?.length) {
            return [];
          }

          const model = this.buildTimelineModel(this.selectedQueueIds);

          return this.homeService.getVisualQueueTimeline(model);
        })
      )
      .subscribe((result: any) => {
        if (result?.returnObject) {
          this.timelineData = result.returnObject;
          this.buildChart();
        }
      });
  }
}